import React from 'react';
import { Box, Typography } from '@mui/material';
import { guideTokens } from '../theme/guideTokens';

type CalloutVariant = 'tip' | 'warning';

interface GuideCalloutProps {
  variant: CalloutVariant;
  items: string[];
}

const STYLES: Record<
  CalloutVariant,
  { label: string; border: (dark: boolean) => string; bg: (dark: boolean) => string; labelColor: string }
> = {
  tip: {
    label: 'Tips',
    border: (dark) => (dark ? 'rgba(51,196,126,0.25)' : 'rgba(30,157,99,0.25)'),
    bg: (dark) => (dark ? 'rgba(51,196,126,0.08)' : 'rgba(30,157,99,0.08)'),
    labelColor: '#1E9D63',
  },
  warning: {
    label: 'Important',
    border: (dark) => (dark ? 'rgba(245,158,11,0.35)' : 'rgba(217,119,6,0.35)'),
    bg: (dark) => (dark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.10)'),
    labelColor: '#D97706',
  },
};

export const GuideCallout: React.FC<GuideCalloutProps> = ({ variant, items }) => {
  if (!items.length) return null;
  const style = STYLES[variant];

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: (t) => style.border(t.palette.mode === 'dark'),
        bgcolor: (t) => style.bg(t.palette.mode === 'dark'),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          fontFamily: guideTokens.fontDisplay,
          color: style.labelColor,
          display: 'block',
          mb: 0.75,
        }}
      >
        {style.label}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {items.map((item) => (
          <Typography
            component="li"
            key={item.slice(0, 48)}
            variant="body2"
            color="text.secondary"
            sx={{ mb: 0.5, lineHeight: 1.55, fontFamily: guideTokens.fontBody }}
          >
            {item}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};
